import type {
  PublicCategoryStatus,
  PublicPlatform,
} from "@/v2-status-client/status/status.types"
import StatusPlatformGroup from "@/v2-status-client/status/platform/StatusPlatformGroup"
import StatusPlatformDetail from "@/v2-status-client/status/platform/StatusPlatformDetail"
import StatusSection from "@/v2-status-client/status/section/StatusSection"
import { STATUS_FILTER_ALL } from "@/v2-status-client/status/status.filter"

export type StatusPlatformListProps = {
  platforms: PublicPlatform[]
  categories: PublicCategoryStatus[]
  // Active filter from the URL. `STATUS_FILTER_ALL` renders every
  // platform side-by-side as a scannable overview; any other value
  // narrows the page to that single platform's detail layout (a
  // standalone aggregate row plus a category accordion).
  activePlatformId: string
  // Active category id from the URL hash, forwarded to the detail
  // layout so a deep-linked category row expands automatically on
  // first render. Empty string when no category is targeted.
  activeCategoryId: string
}

// "Services" section. Two render modes driven by the active filter:
//
//   - All platforms (overview): every platform card stacked, each
//     showing its category summary rows only. Optimised for
//     scanning across applications -- the customer sees four
//     platform headers and their category strips at a glance, no
//     accordion clutter.
//
//   - Single platform (detail): the matching platform's aggregate
//     row at the top, then the platform's categories rendered as
//     collapsible cards below. The aggregate row is intentionally
//     a sibling (not a wrapper) so it doesn't trap the categories
//     inside another layer of nesting; customer feedback was that
//     the previous nested-card-in-card layout felt cluttered.
//
// Categories whose platformId isn't in the platforms list (forward-
// rollout windows where a new platform ships before the frontend
// has been redeployed) fall through to a synthesised "Other
// services" platform so nothing is silently dropped.
function StatusPlatformList(props: Readonly<StatusPlatformListProps>) {
  const { platforms, categories, activePlatformId, activeCategoryId } = props
  const isOverview = activePlatformId === STATUS_FILTER_ALL
  const categoriesByPlatform = groupCategoriesByPlatformId(categories)
  const orphans = pickOrphanCategories(platforms, categories)

  if (isOverview) {
    return (
      <StatusSection
        title="Services"
        aside="Status of every application Placepoint operates or monitors"
      >
        <div className="flex flex-col gap-6">
          {platforms.map((p) => (
            <StatusPlatformGroup
              key={p.id}
              platform={p}
              categories={categoriesByPlatform.get(p.id) ?? []}
            />
          ))}
          {orphans.length > 0 && (
            <StatusPlatformGroup
              platform={buildOrphanPlatform(orphans)}
              categories={orphans}
            />
          )}
          {platforms.length === 0 && orphans.length === 0 && (
            <StatusPlatformListEmpty />
          )}
        </div>
      </StatusSection>
    )
  }

  // Single-platform detail view. Match against the real platforms
  // first, then fall back to the synthesised orphan card so an
  // explicit `?platform=uncategorised` link keeps working.
  const platform = platforms.find((p) => p.id === activePlatformId)
  if (platform) {
    return (
      <StatusSection
        title="Services"
        aside="Detailed status for every monitored service in this platform"
      >
        <StatusPlatformDetail
          platform={platform}
          categories={categoriesByPlatform.get(platform.id) ?? []}
          activeCategoryId={activeCategoryId}
        />
      </StatusSection>
    )
  }
  if (activePlatformId === ORPHAN_PLATFORM_ID && orphans.length > 0) {
    return (
      <StatusSection
        title="Services"
        aside="Detailed status for every monitored service in this platform"
      >
        <StatusPlatformDetail
          platform={buildOrphanPlatform(orphans)}
          categories={orphans}
          activeCategoryId={activeCategoryId}
        />
      </StatusSection>
    )
  }
  return (
    <StatusSection
      title="Services"
      aside="Detailed status for every monitored service in this platform"
    >
      <StatusPlatformListEmpty />
    </StatusSection>
  )
}

// Stable id used both as the synthesised platform's id and as the
// filter value for the orphan card's pill -- so a customer can
// deep-link to it the same way as any real platform.
const ORPHAN_PLATFORM_ID = "uncategorised"

// Inline empty-state copy. Pulled out so both the overview and
// detail branches read the same wording without duplicating the
// surrounding panel chrome.
function StatusPlatformListEmpty() {
  return (
    <p className="rounded-md border border-border/60 bg-background/30 px-4 py-6 text-center text-sm text-muted-foreground">
      No platform matches the current filter.
    </p>
  )
}

// groupCategoriesByPlatformId fans the flat category list into a
// {platformId -> categories} map so each platform group can render
// its slice without scanning the full list. Iteration order of the
// input array is preserved per platform so the rendered category
// order matches what the backend returned.
function groupCategoriesByPlatformId(
  categories: PublicCategoryStatus[],
): Map<string, PublicCategoryStatus[]> {
  const out = new Map<string, PublicCategoryStatus[]>()
  for (const c of categories) {
    const list = out.get(c.platformId) ?? []
    list.push(c)
    out.set(c.platformId, list)
  }
  return out
}

// pickOrphanCategories returns categories whose platformId isn't in
// the platforms list. Should be empty under normal operation; gives
// us a safety net during forward-rollout windows where the API ships
// a new platform before the frontend learns about it.
function pickOrphanCategories(
  platforms: PublicPlatform[],
  categories: PublicCategoryStatus[],
): PublicCategoryStatus[] {
  const known = new Set(platforms.map((p) => p.id))
  return categories.filter((c) => !known.has(c.platformId))
}

// buildOrphanPlatform synthesises a stand-in platform card so the
// orphan group still renders with the same DOM shape as known
// platforms. Status / counts are summed straight off the orphan
// categories so the readout stays accurate.
function buildOrphanPlatform(orphans: PublicCategoryStatus[]): PublicPlatform {
  const totals = orphans.reduce(
    (acc, c) => {
      acc.total += c.totalCount
      acc.up += c.operational
      acc.degraded += c.degraded
      acc.down += c.down
      return acc
    },
    { total: 0, up: 0, degraded: 0, down: 0 },
  )
  const status: PublicPlatform["status"] =
    totals.down > 0 ? "down" : totals.degraded > 0 ? "degraded" : "operational"
  return {
    id: ORPHAN_PLATFORM_ID,
    name: "Other services",
    description: "Services not yet grouped under a known application",
    status,
    summary:
      totals.total === 0
        ? "No services configured yet"
        : totals.down > 0
          ? `${totals.down} of ${totals.total} unavailable`
          : totals.degraded > 0
            ? `${totals.degraded} of ${totals.total} slower than usual`
            : "All systems operational",
    operational: totals.up,
    degraded: totals.degraded,
    down: totals.down,
    totalCount: totals.total,
    uptimePercent30d: null,
    history: orphans[0]?.history ?? [],
    categoryIds: orphans.map((c) => c.id),
  }
}

export default StatusPlatformList
